import { Component, ElementRef, ViewEncapsulation, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { lastValueFrom } from 'rxjs';

import { ConciergeApiService } from 'src/shared/services/concierge-api.service';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { WindowService } from 'src/shared/providers/window.service';
import { Logger } from 'src/providers/logger';
import { Client } from 'src/providers/client/client';

import { ConciergeItemModel } from 'src/models/concierge.model';
import { environment } from 'src/environments/environment';
import { Util } from 'src/shared/util/util';
import { buildCommentFeedbackPayload } from 'src/shared/util/comment-feedback';

@Component({
    templateUrl: 'concierge-message.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class ConciergeMessagePage implements OnInit {

  email: string | null;
  errorMessage: string;
  item: ConciergeItemModel;
  pageTitle: string;
  replyTime: string;
  status: string;
  statusIcon: string | undefined;
  createdAt: Date;
  jobData: any;
  isJobIssue: boolean;
  loaded: boolean;


  activeFeedbackCommentId: number | null = null;
  closingFeedbackCommentId: number | null = null;
  activeFeedbackType: 'positive' | 'negative' = 'positive';
  isSubmittingFeedback = false;

  boundReply = this.reply.bind(this);

  private readonly conciergeApi = inject(ConciergeApiService);
  private readonly client = inject(Client);
  private readonly el = inject(ElementRef);
  private readonly logger = inject(Logger);
  private readonly navCtrl = inject(CustomNavController);
  private readonly route = inject(ActivatedRoute);
  private readonly util = inject(Util);
  readonly windowService = inject(WindowService);

  constructor() {}

  async ngOnInit() {
    try {
      this.loaded = false;
      this.errorMessage = '';
      const navigationItem = this.navCtrl.getParam('item') as ConciergeItemModel | undefined;
      const conciergeItemId = navigationItem?.id || parseInt(this.route.snapshot.paramMap.get('conciergeItemId'));
      try {
        this.item = await lastValueFrom(this.conciergeApi.getConciergeItemById(conciergeItemId));
      } catch (err) {
        this.logger.error(err);
        this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      }
      if (!this.item) {
        this.errorMessage = this.errorMessage || 'We couldn\'t load this conversation. Please try again later.';
        return;
      }
      // Loading the client email is best-effort; a failure here should not blank the page.
      try {
        this.email = localStorage.getItem('clientEmail');
        if (!this.email) {
          await this.setClientInfo();
          this.email = localStorage.getItem('clientEmail');
        }
      } catch (err) {
        this.logger.error(err);
      }
      this.isJobIssue = this.item.type === 'support.job_issue' && (this.item?.metadata?.custom?.job_id ?? null) !== null;
      this.pageTitle = this.getPageTitle();
      this.status = this.item?.components?.[0]?.value?.status;
      this.statusIcon = this.conciergeApi.getStatusIcon(this.status);
      this.replyTime = this.conciergeApi.getReplyTime(this.item.type);
      this.loaded = true;
    } catch (err) {
      this.logger.error(err);
      this.errorMessage = this.errorMessage || (err?.error?.message || err?.message || 'Something went wrong loading this conversation.');
    }
  }

  goBack() {
    this.navCtrl.navigateRoot('concierge');
  }

  getPageTitle() {
    switch (this.item.type) {
      case 'support.other':
        return 'Other Question';
      case 'support.how_to':
        return 'How to Use TIDY';
      case 'support.job_issue':
        return 'Job Issue';
      case 'support.billing':
        return 'Billing Issue';
      case 'support.developer':
        return 'Developer Question';
      case 'support.integration':
        return 'Marked Completed';
      case 'support.concierge_task':
        return 'Concierge Action';
    }
  }

  goToJobDetails() {
    if (this.item.metadata.custom.privateJob) {
      this.navCtrl.navigateForward(`past-private-job/${this.item.metadata.custom.job_id}`);
    } else {
      this.navCtrl.navigateForward(`past-job/${this.item.metadata.custom.job_id}`);
    }
  }

  async reply() {
    const params = {
      item: this.item,
      itemId: this.item?.id || parseInt(this.route.snapshot.paramMap.get('conciergeItemId'))
    };
    this.navCtrl.navigateForward('concierge-reply', params);
  }

  openAttachment(attachmentKey: string) {
    const fileUrl = `${environment.aws_s3_bucket_url}${attachmentKey}`;
    this.util.openUrl(fileUrl);
  }

  onCommentThumbClick(comment: any, type: 'positive' | 'negative'): void {
    this.activeFeedbackCommentId = comment.id;
    this.activeFeedbackType = type;
  }

  cancelCommentFeedback(): void {
    this.dismissCommentFeedback();
  }

  async submitCommentFeedback(comment: any): Promise<void> {
    const textarea = this.el.nativeElement.querySelector('.concierge-feedback-textarea') as HTMLTextAreaElement;
    const feedbackText = textarea?.value?.trim();
    this.isSubmittingFeedback = true;
    const data = buildCommentFeedbackPayload({
      commentId: comment.id,
      conciergeItemId: this.item.id,
      type: this.activeFeedbackType,
      text: feedbackText,
    });
    try {
      await lastValueFrom(this.conciergeApi.submitFeedback(data));
      this.dismissCommentFeedback();
      this.util.showSuccess('Feedback submitted');
    } catch (err: any) {
      this.util.showError(err?.error?.message || err?.message || 'Failed to submit feedback');
    } finally {
      this.isSubmittingFeedback = false;
    }
  }

  private dismissCommentFeedback(): void {
    this.closingFeedbackCommentId = this.activeFeedbackCommentId;
    setTimeout(() => {
      this.activeFeedbackCommentId = null;
      this.closingFeedbackCommentId = null;
    }, 200);
  }

  private async setClientInfo(): Promise<void> {
    const customerId = parseInt(localStorage.getItem('customer_id'));
    const clientData = await this.client.getCustomerInfo(customerId);
    localStorage.setItem('clientEmail', clientData.email);
    localStorage.setItem('clientFirstName', clientData.first_name);
  }
}
